import { useNavigate } from 'react-router-dom';
import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import Header from "../components/HeaderA";
import { translateServerMsg } from '../utils/serverMessages';

const translations = {
  el: {
    registerTitle: "Δημιουργία λογαριασμού",
    username: "Όνομα χρήστη",
    email: "Email",
    name: "Όνομα",
    lastName: "Επώνυμο",
    password: "Κωδικός πρόσβασης",
    placeholderUser: "Εισάγετε όνομα χρήστη",
    placeholderEmail: "Εισάγετε email",
    placeholderName: "Εισάγετε το όνομά σας",
    placeholderLastName: "Εισάγετε το επώνυμό σας",
    placeholderPass: "Εισάγετε κωδικό",
    registerBtn: "Εγγραφή",
    regFailed: "Η εγγραφή απέτυχε",
    errorTitle: "Σφάλμα εγγραφής",
    ok: "Εντάξει"
  },
  en: {
    registerTitle: "Create account",
    username: "Username",
    email: "Email",
    name: "Name",
    lastName: "Last name",
    password: "Password",
    placeholderUser: "Enter username",
    placeholderEmail: "Enter email",
    placeholderName: "Enter your name",
    placeholderLastName: "Enter your last name",
    placeholderPass: "Enter password",
    registerBtn: "Register",
    regFailed: "Registration failed",
    errorTitle: "Registration error",
    ok: "OK"
  }
};

const Register = () => {
  const { login } = useContext(AuthContext);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [full_name, setFullName] = useState('');
  const [last_name, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [popup, setPopup] = useState(null);
  const navigate = useNavigate();

  //State localStorage language
  const [lang, setLang] = useState(localStorage.getItem("app_lang") || localStorage.getItem("language") || "el");

  //Listener for localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      setLang(localStorage.getItem("app_lang") || localStorage.getItem("language") || "el");
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const t = translations[lang];

  const handleRegister = async (e) => {
    e.preventDefault();
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, email, password, full_name, last_name })
    });

    const data = await res.json();

    if (!res.ok) {
      if (data.errors && Array.isArray(data.errors)) {
        const formattedErrors = {};
        data.errors.forEach(err => {
          const field = err.path || err.param;
          formattedErrors[field] = err.msg;
        });
        setErrors(formattedErrors);
      } else if (data.msg) {
        setPopup({ title: t.errorTitle, msg: translateServerMsg(data.msg, lang) });
      } else {
        setPopup({ title: t.errorTitle, msg: t.regFailed });
        resetForm();
      }
      return;
    }

    if (data.accessToken && data.refreshToken) {
      login(data.accessToken, data.refreshToken);
      // Navigate based on user role
      if (data.user.role === 'admin') {
        navigate('/adminmenu');
      } else {
        navigate('/customermenu');
      }
    } else {
      setPopup({ title: t.errorTitle, msg: t.regFailed });
      resetForm();
    }
  };

  const resetForm = () => {
    setUsername('');
    setEmail('');
    setFullName('');
    setLastName('');
    setPassword('');
  };

  return (
    <div className="min-h-screen bg-custom-background flex flex-col">
      <Header />

      <main id="main-content" className="flex-1 flex items-center justify-center px-4 py-8">
        <form 
          onSubmit={handleRegister}
          aria-labelledby="register-form-title"
          noValidate
          className="w-full max-w-md bg-gradient-to-b from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 p-6 rounded-3xl shadow-lg flex flex-col gap-4"
        >
          <h1 id="register-form-title" className="text-xl font-bold text-white text-center mb-2">{t.registerTitle}</h1>

          <label htmlFor="reg-username" className="text-white font-semibold">{t.username}</label>
          <input 
            id="reg-username"
            type="text" 
            placeholder={t.placeholderUser} 
            value={username} 
            onChange={e => setUsername(e.target.value)}
            required
            autoComplete="username"
            aria-describedby={errors.username ? "reg-username-error" : undefined}
            className="p-2 rounded-lg text-black outline-none focus:ring-2 focus:ring-custom-header"
          />
          {errors.username && (
            <p id="reg-username-error" role="alert" className="text-white text-sm mt-1">{errors.username}</p>
          )}

          <label htmlFor="reg-email" className="text-white font-semibold">{t.email}</label>
          <input 
            id="reg-email"
            type="email" 
            placeholder={t.placeholderEmail} 
            value={email} 
            onChange={e => setEmail(e.target.value)}
            required
            autoComplete="email"
            aria-describedby={errors.email ? "reg-email-error" : undefined}
            className="p-2 rounded-lg text-black outline-none focus:ring-2 focus:ring-custom-header"
          />
          {errors.email && (
            <p id="reg-email-error" role="alert" className="text-white text-sm mt-1">{errors.email}</p>
          )}
          
          <label htmlFor="reg-fullname" className="text-white font-semibold">{t.name}</label>
          <input 
            id="reg-fullname"
            type="text" 
            placeholder={t.placeholderName} 
            value={full_name} 
            onChange={e => setFullName(e.target.value)}
            autoComplete="given-name"
            aria-describedby={errors.full_name ? "reg-fullname-error" : undefined}
            className="p-2 rounded-lg text-black outline-none focus:ring-2 focus:ring-custom-header"
          />
          {errors.full_name && (
            <p id="reg-fullname-error" role="alert" className="text-white text-sm mt-1">{errors.full_name}</p>
          )}
          
          <label htmlFor="reg-lastname" className="text-white font-semibold">{t.lastName}</label>
          <input 
            id="reg-lastname"
            type="text" 
            placeholder={t.placeholderLastName} 
            value={last_name} 
            onChange={e => setLastName(e.target.value)}
            autoComplete="family-name"
            aria-describedby={errors.last_name ? "reg-lastname-error" : undefined}
            className="p-2 rounded-lg text-black outline-none focus:ring-2 focus:ring-custom-header"
          />
          {errors.last_name && (
            <p id="reg-lastname-error" role="alert" className="text-white text-sm mt-1">{errors.last_name}</p>
          )}
          
          <label htmlFor="reg-password" className="text-white font-semibold">{t.password}</label>
          <input 
            id="reg-password"
            type="password" 
            placeholder={t.placeholderPass} 
            value={password} 
            onChange={e => setPassword(e.target.value)}
            required
            autoComplete="new-password"
            aria-describedby={errors.password ? "reg-password-error" : undefined}
            className="p-2 rounded-lg text-black outline-none focus:ring-2 focus:ring-custom-header"
          />
          {errors.password && (
            <p id="reg-password-error" role="alert" className="text-white text-sm mt-1">{errors.password}</p>
          )}
          
          <button 
            type="submit" 
            className="bg-custom-header text-custom-letters font-bold py-2 rounded-lg mt-2 hover:bg-gray-200 transition"
          >
            {t.registerBtn}
          </button>
        </form>
      </main>

      {popup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]">
          <div role="dialog" aria-modal="true" aria-labelledby="register-popup-title" className="bg-custom-header rounded-2xl p-6 w-80 shadow-xl text-center">
            <h3 id="register-popup-title" className="text-lg font-semibold text-custom-letters mb-3">{popup.title}</h3>
            <p className="text-sm text-custom-letters mb-6">{popup.msg}</p>
            <button
              onClick={() => setPopup(null)}
              className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-custom-letters font-bold">
              {t.ok}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;